import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import rhService, { Employe, EmployeRequest, SituationFamiliale, TypeContrat } from '../../api/rh';
import { formatTND } from '../../utils/formatters';

const CONTRATS: TypeContrat[] = ['CDI', 'CDD', 'SIVP', 'KARAMA', 'STAGE', 'INTERIM'];
const SITUATIONS: SituationFamiliale[] = ['CELIBATAIRE', 'MARIE', 'DIVORCE', 'VEUF'];

const emptyForm = (): EmployeRequest => ({
  matricule: '',
  nom: '',
  prenom: '',
  cin: '',
  numeroCnss: '',
  poste: '',
  departement: '',
  email: '',
  telephone: '',
  rib: '',
  dateEmbauche: '',
  typeContrat: 'CDI',
  situationFamiliale: 'CELIBATAIRE',
  chefDeFamille: false,
  nbEnfants: 0,
  salaireBaseBrut: 0,
  congesAnnuels: 30,
  actif: true,
});

const Employes: React.FC = () => {
  const [employes, setEmployes] = useState<Employe[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<EmployeRequest>(emptyForm());
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    rhService
      .listEmployes(false)
      .then(setEmployes)
      .catch((e: any) => {
        if (e?.response?.status !== 403) toast.error('Chargement impossible.');
      });

  useEffect(() => {
    refresh();
  }, []);

  const set = (k: keyof EmployeRequest, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const ouvrirCreation = () => {
    setEditId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const ouvrirEdition = (e: Employe) => {
    setEditId(e.id);
    setForm({
      matricule: e.matricule,
      nom: e.nom,
      prenom: e.prenom,
      cin: e.cin || '',
      numeroCnss: e.numeroCnss || '',
      poste: e.poste || '',
      departement: e.departement || '',
      email: e.email || '',
      telephone: e.telephone || '',
      rib: e.rib || '',
      dateEmbauche: e.dateEmbauche || '',
      dateSortie: e.dateSortie || '',
      typeContrat: e.typeContrat,
      situationFamiliale: e.situationFamiliale,
      chefDeFamille: e.chefDeFamille,
      nbEnfants: e.nbEnfants,
      salaireBaseBrut: e.salaireBaseBrut,
      congesAnnuels: e.congesAnnuels,
      actif: e.actif,
    });
    setShowForm(true);
  };

  const enregistrer = async () => {
    if (!form.nom?.trim() || !form.prenom?.trim()) {
      toast.error('Nom et prénom obligatoires.');
      return;
    }
    setBusy(true);
    try {
      const payload: EmployeRequest = {
        ...form,
        salaireBaseBrut: Number(form.salaireBaseBrut) || 0,
        nbEnfants: Number(form.nbEnfants) || 0,
        congesAnnuels: Number(form.congesAnnuels) || 0,
      };
      if (editId) {
        await rhService.updateEmploye(editId, payload);
        toast.success('Salarié mis à jour.');
      } else {
        await rhService.createEmploye(payload);
        toast.success('Salarié ajouté.');
      }
      setShowForm(false);
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Échec de l'enregistrement.");
    } finally {
      setBusy(false);
    }
  };

  const supprimer = async (e: Employe) => {
    if (!window.confirm(`Supprimer ${e.nomComplet} ?`)) return;
    try {
      await rhService.deleteEmploye(e.id);
      toast.success('Salarié supprimé.');
      refresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Suppression impossible.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Salariés</h1>
          <p className="text-sm text-gray-500">
            Répertoire du personnel. <Link to="/rh" className="text-blue-600 hover:underline">← Module RH</Link>
          </p>
        </div>
        <button
          onClick={ouvrirCreation}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
        >
          + Nouveau salarié
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-4">
          <h2 className="text-lg font-semibold">{editId ? 'Modifier le salarié' : 'Nouveau salarié'}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Matricule (auto si vide)">
              <input value={form.matricule || ''} onChange={(e) => set('matricule', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Nom *">
              <input value={form.nom || ''} onChange={(e) => set('nom', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Prénom *">
              <input value={form.prenom || ''} onChange={(e) => set('prenom', e.target.value)} className={inputCls} />
            </Field>
            <Field label="CIN">
              <input value={form.cin || ''} onChange={(e) => set('cin', e.target.value)} className={inputCls} />
            </Field>
            <Field label="N° CNSS">
              <input value={form.numeroCnss || ''} onChange={(e) => set('numeroCnss', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Poste">
              <input value={form.poste || ''} onChange={(e) => set('poste', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Département">
              <input value={form.departement || ''} onChange={(e) => set('departement', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Email">
              <input value={form.email || ''} onChange={(e) => set('email', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Téléphone">
              <input value={form.telephone || ''} onChange={(e) => set('telephone', e.target.value)} className={inputCls} />
            </Field>
            <Field label="RIB">
              <input value={form.rib || ''} onChange={(e) => set('rib', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Date d'embauche">
              <input type="date" value={form.dateEmbauche || ''} onChange={(e) => set('dateEmbauche', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Type de contrat">
              <select value={form.typeContrat} onChange={(e) => set('typeContrat', e.target.value)} className={inputCls}>
                {CONTRATS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Situation familiale">
              <select value={form.situationFamiliale} onChange={(e) => set('situationFamiliale', e.target.value)} className={inputCls}>
                {SITUATIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Enfants à charge">
              <input type="number" min={0} value={form.nbEnfants ?? 0} onChange={(e) => set('nbEnfants', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Salaire de base brut (mensuel)">
              <input type="number" step="0.001" value={form.salaireBaseBrut ?? 0} onChange={(e) => set('salaireBaseBrut', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Droit congés payés (jours/an)">
              <input type="number" step="0.5" value={form.congesAnnuels ?? 30} onChange={(e) => set('congesAnnuels', e.target.value)} className={inputCls} />
            </Field>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!form.chefDeFamille} onChange={(e) => set('chefDeFamille', e.target.checked)} />
              <span className="text-sm">Chef de famille</span>
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!form.actif} onChange={(e) => set('actif', e.target.checked)} />
              <span className="text-sm">Actif</span>
            </label>
          </div>
          <div className="flex gap-3">
            <button onClick={enregistrer} disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
              {busy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-200 rounded-lg">
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3">Matricule</th>
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3">Poste</th>
              <th className="px-4 py-3">Contrat</th>
              <th className="px-4 py-3 text-right">Salaire base</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {employes.map((e) => (
              <tr key={e.id} className="border-t border-gray-100">
                <td className="px-4 py-3 font-mono text-xs">{e.matricule}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{e.nomComplet}</td>
                <td className="px-4 py-3">{e.poste || '—'}</td>
                <td className="px-4 py-3">{e.typeContrat}</td>
                <td className="px-4 py-3 text-right">{formatTND(e.salaireBaseBrut)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${e.actif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {e.actif ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-3">
                  <button onClick={() => ouvrirEdition(e)} className="text-blue-600 hover:underline">Modifier</button>
                  <button onClick={() => supprimer(e)} className="text-red-600 hover:underline">Supprimer</button>
                </td>
              </tr>
            ))}
            {employes.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">Aucun salarié.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    {children}
  </div>
);

export default Employes;
